import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Theme,
  Typography,
  useTheme,
} from "@material-ui/core";
import React, { useCallback, useContext } from "react";
import { Identifier, Translate, useRefresh, useTranslate } from "react-admin";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { useAppData } from "../../../common/hooks/useAppData";
import FolderSharedOutlinedIcon from "@material-ui/icons/FolderSharedOutlined";
import { IDropDownAppData } from "../../../common/redux/reducers/appDataReducer";
import { createTenantIdSetAction } from "../../../common/redux/actions/tenantId";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";
import { useSelectedTenant } from "../../../common/hooks/useSelectedTenant";
import { AppBarFiltersContainerContext } from "../app-bar-filters/AppBarFiltersContainerContext";

interface Props {}

const renderEmptyTenantAppbarFilter = (
  header: boolean,
  theme: Theme,
  translate: Translate
) => (
  <MenuItem className="appbar-filter-list-item" value="">
    <ListItemIcon
      style={{ color: header ? theme.palette.common.white : "auto" }}>
      <FolderSharedOutlinedIcon />
    </ListItemIcon>
    <ListItemText>
      <Typography variant="body2">
        {translate("resources.common.labels.selectTenant")}
      </Typography>
    </ListItemText>
  </MenuItem>
);

const renderTenantAppbarFilterValue = (
  id: Identifier,
  header: boolean,
  theme: Theme,
  tenants: IDropDownAppData<string>[]
) => (
  <MenuItem key={id} className="appbar-filter-list-item" value={id}>
    <ListItemIcon
      style={{ color: header ? theme.palette.common.white : "auto" }}>
      <FolderSharedOutlinedIcon />
    </ListItemIcon>
    <ListItemText>
      <Typography variant="body2">
        {tenants.find((x) => x.id === id)?.name}
      </Typography>
    </ListItemText>
  </MenuItem>
);

export const AppBarTenantSelector = (props: Props) => {
  const theme: Theme = useTheme();
  const translate = useTranslate();
  const tenants = useAppData((x) => x.tenants) ?? [];
  let selectedTenantInfo = useSelectedTenant();

  const dispatch = useDispatch();
  const axios = useAxiosInstance();
  const refresh = useRefresh();

  const handleTenantChange = useCallback(
    (
      event: React.ChangeEvent<{
        name?: string | undefined;
        value: any;
      }>,
      _: React.ReactNode
    ) => {
      if (axios) {
        dispatch(
          createTenantIdSetAction({
            tenantId: event.target.value,
          })
        );

        refresh();
      }
    },
    [axios, refresh, dispatch]
  );

  const { container: appbarFiltersContainer } = useContext(
    AppBarFiltersContainerContext
  );

  return appbarFiltersContainer
    ? createPortal(
        <>
          <Select
            autoFocus={false}
            className="appbar-filter"
            disableUnderline
            label=""
            value={selectedTenantInfo.tenantId ?? ""}
            displayEmpty
            renderValue={(value) => {
              if (!value || value === "") {
                return renderEmptyTenantAppbarFilter(true, theme, translate);
              }

              return renderTenantAppbarFilterValue(
                value as Identifier,
                true,
                theme,
                tenants
              );
            }}
            onChange={handleTenantChange}>
            {renderEmptyTenantAppbarFilter(false, theme, translate)}
            {tenants.map((tenant) =>
              renderTenantAppbarFilterValue(
                tenant.id ?? "",
                false,
                theme,
                tenants
              )
            )}
          </Select>
        </>,
        appbarFiltersContainer
      )
    : null;
};
